import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import remediationService from '../../../services/remediationService';
import RemediationStatisticsCard from './RemediationStatisticsCard';
import ViolationsList from './ViolationsList';
import CorrectionsList from './CorrectionsList';
import RemediationActionsList from './RemediationActionsList';

const RemediationStatusPanel = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [statistics, setStatistics] = useState(null);
  const [violations, setViolations] = useState([]);
  const [corrections, setCorrections] = useState([]);
  const [remediationActions, setRemediationActions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const [statsData, violationsData, correctionsData, actionsData] = await Promise.all([
        remediationService?.getRemediationStatistics(),
        remediationService?.getAllViolations(),
        remediationService?.getViolationCorrections(),
        remediationService?.getRemediationActions()
      ]);

      setStatistics(statsData);
      setViolations(violationsData);
      setCorrections(correctionsData);
      setRemediationActions(actionsData);
    } catch (err) {
      setError(err?.message || 'Failed to load remediation data');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateViolationStatus = async (violationId, newStatus) => {
    try {
      await remediationService?.updateViolationStatus(violationId, newStatus);
      await loadData();
    } catch (err) {
      setError(err?.message || 'Failed to update violation status');
    }
  };

  const handleUpdateCorrectionStatus = async (correctionId, newStatus, updates = {}) => {
    try {
      await remediationService?.updateCorrectionStatus(correctionId, newStatus, updates);
      await loadData();
    } catch (err) {
      setError(err?.message || 'Failed to update correction status');
    }
  };

  const handleUpdateRemediationStatus = async (actionId, newStatus, updates = {}) => {
    try {
      await remediationService?.updateRemediationStatus(actionId, newStatus, updates);
      await loadData();
    } catch (err) {
      setError(err?.message || 'Failed to update remediation status');
    }
  };

  const severityFilterOptions = [
    { value: 'all', label: 'All Severities' },
    { value: 'critical', label: 'Critical' },
    { value: 'high', label: 'High' },
    { value: 'medium', label: 'Medium' },
    { value: 'low', label: 'Low' }
  ];

  const statusFilterOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'identified', label: 'Identified' },
    { value: 'pending_review', label: 'Pending Review' },
    { value: 'approved', label: 'Approved' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' }
  ];

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'violations', label: 'Violations', icon: 'AlertTriangle' },
    { id: 'corrections', label: 'AI Corrections', icon: 'Sparkles' },
    { id: 'actions', label: 'Remediation Actions', icon: 'CheckSquare' }
  ];

  const filteredViolations = violations?.filter(v => {
    if (severityFilter !== 'all' && v?.severity !== severityFilter) return false;
    if (statusFilter !== 'all' && v?.violationStatus !== statusFilter) return false;
    return true;
  });

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="p-4 md:p-6 border-b border-border">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Icon name="Shield" size={20} color="var(--color-primary)" />
            <h3 className="font-heading font-semibold text-base md:text-lg text-foreground">
              Remediation Status Panel
            </h3>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={loadData}
            disabled={loading}
            iconName={loading ? 'Loader2' : 'RefreshCw'}
            iconPosition="left"
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>
        <p className="caption text-xs md:text-sm text-muted-foreground">
          Track AI-suggested corrections, backpay calculations, and implementation progress for compliance violations
        </p>
      </div>

      {error && (
        <div className="p-4 bg-error/10 border-b border-error">
          <div className="flex items-center gap-2">
            <Icon name="AlertCircle" size={20} color="var(--color-error)" />
            <p className="text-error font-medium text-sm">{error}</p>
          </div>
        </div>
      )}

      <div className="p-4 border-b border-border">
        <div className="flex items-center gap-2 overflow-x-auto pb-2">
          {tabs?.map(tab => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-caption text-sm transition-smooth whitespace-nowrap ${
                activeTab === tab?.id
                  ? 'bg-primary text-white' :'bg-muted text-foreground hover:bg-muted/80'
              }`}
            >
              <Icon name={tab?.icon} size={16} />
              {tab?.label}
            </button>
          ))}
        </div>
      </div>

      <div className="p-4 md:p-6">
        {loading && activeTab === 'overview' && (
          <div className="flex items-center justify-center py-12">
            <Icon name="Loader2" size={48} color="var(--color-primary)" className="animate-spin" />
          </div>
        )}

        {!loading && activeTab === 'overview' && (
          <div className="space-y-6">
            <RemediationStatisticsCard statistics={statistics} />
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-muted/50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Icon name="AlertTriangle" size={18} color="var(--color-error)" />
                  <h4 className="font-heading font-semibold text-sm text-foreground">
                    Critical Violations
                  </h4>
                </div>
                <p className="text-2xl font-heading font-bold text-error mb-1">
                  {statistics?.criticalViolations || 0}
                </p>
                <p className="caption text-xs text-muted-foreground">
                  Require immediate attention
                </p>
              </div>

              <div className="bg-muted/50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Icon name="DollarSign" size={18} color="var(--color-warning)" />
                  <h4 className="font-heading font-semibold text-sm text-foreground">
                    Pending Backpay
                  </h4>
                </div>
                <p className="text-2xl font-heading font-bold text-warning mb-1">
                  K{(statistics?.totalBackpayRequired || 0)?.toFixed(2)?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                </p>
                <p className="caption text-xs text-muted-foreground">
                  Total backpay to be processed
                </p>
              </div>
            </div>

            <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Icon name="Info" size={20} color="var(--color-primary)" />
                <div>
                  <h4 className="font-heading font-semibold text-sm text-foreground mb-1">
                    Remediation Progress
                  </h4>
                  <p className="caption text-xs text-muted-foreground mb-3">
                    {statistics?.completedRemediations || 0} of {statistics?.totalViolations || 0} violations remediated
                  </p>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div
                      className="bg-primary h-2 rounded-full transition-smooth"
                      style={{
                        width: `${statistics?.totalViolations > 0 ? ((statistics?.completedRemediations / statistics?.totalViolations) * 100) : 0}%`
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'violations' && (
          <div>
            <div className="flex items-center gap-3 mb-4">
              <Select
                options={severityFilterOptions}
                value={severityFilter}
                onChange={setSeverityFilter}
                className="w-48"
              />
              <Select
                options={statusFilterOptions}
                value={statusFilter}
                onChange={setStatusFilter}
                className="w-48"
              />
            </div>
            <ViolationsList
              violations={filteredViolations}
              onUpdateStatus={handleUpdateViolationStatus}
              loading={loading}
            />
          </div>
        )}

        {activeTab === 'corrections' && (
          <CorrectionsList
            corrections={corrections}
            onUpdateStatus={handleUpdateCorrectionStatus}
            loading={loading}
          />
        )}

        {activeTab === 'actions' && (
          <RemediationActionsList
            actions={remediationActions}
            onUpdateStatus={handleUpdateRemediationStatus}
            loading={loading}
          />
        )}
      </div>
    </div>
  );
};

export default RemediationStatusPanel;